/**
 * Drafts to send (UX round 2026-10-20, More ▾): every question Merlin drafted
 * for a colleague that waits on you, across all items, longest-waiting item
 * first. Each one can be sent, changed first or thrown away right here (the
 * same card the item's asks list uses); the item number opens the item.
 *
 * A case explorer asked to work through Merlin's drafts tried five places
 * before asking Merlin, whose count then changed three times. This list
 * reads the door's own asks poll, so its count is the one the More menu
 * shows.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { DraftAskCard } from '../../components/quality/DraftAskCard';
import { trackEvent } from '../../services/portalTracking';
import { useQualityTriage } from './qualityTriageContext';
import { fromTab } from './qualityWorkspaceView';
import {
  draftItemOrder,
  draftItems,
  draftedWhen,
  draftsCountLine,
  draftsMayBeCut,
  type DraftItem,
} from './draftsView';
import pageStyles from './QualityTriagePages.module.css';
import styles from './QualityDraftsPage.module.css';

export default function QualityDraftsPage() {
  const { invState, userName, dataRequests } = useQualityTriage();
  const navigate = useNavigate();
  const requests = dataRequests?.requests;
  const items = useMemo(
    () => draftItems(requests ?? [], invState.threads, invState.investigations, userName),
    [requests, invState.threads, invState.investigations, userName],
  );
  const order = draftItemOrder(items);

  const open = (item: DraftItem) => {
    if (!item.path) return;
    trackEvent('action', 'qcp_drafts_open_item', {
      item_no: item.no,
      drafts: item.questions.length,
      result: 'navigated',
    });
    navigate(`../${item.path}`, fromTab('drafts', order));
  };

  if (!dataRequests || dataRequests.available === false) {
    return (
      <div className={pageStyles.page}>
        <p className={pageStyles.emptyNote}>Merlin’s drafts cannot be read on this server.</p>
      </div>
    );
  }

  return (
    <div className={pageStyles.page}>
      <h2 className={pageStyles.lane} style={{ marginTop: 0 }}>
        Drafts to send
      </h2>
      <p className={pageStyles.laneSub} data-testid="drafts-count">
        Questions Merlin wrote to colleagues. Nothing has gone out: send each one, change it first, or
        throw it away. {draftsCountLine(items)}
      </p>
      {draftsMayBeCut(requests ?? []) && (
        <p className={pageStyles.footNote} data-testid="drafts-cut">
          This reads the newest 200 asks, so a draft older than those is not listed.
        </p>
      )}
      {items.map((item) => (
        <section key={item.key} className={styles.item} aria-label={item.no || item.title} data-testid="drafts-item">
          <header className={styles.itemHead}>
            {item.path ? (
              <button
                type="button"
                className={styles.itemNo}
                onClick={() => open(item)}
                title={`Open ${item.no}`}
              >
                {item.no}
              </button>
            ) : null}
            <span className={styles.itemTitle}>{item.title}</span>
            {item.where && <span className={styles.itemWhere}>{item.where}</span>}
            <span className={styles.itemAge}>{draftedWhen(item.oldest)}</span>
          </header>
          {item.questions.map((q) => (
            <DraftAskCard
              key={q.key}
              request={q.lead}
              others={q.others}
              onRequestUpdated={dataRequests.applyUpdate}
              trackContext={{ surface: 'drafts_page', item_no: item.no || null }}
            />
          ))}
        </section>
      ))}
    </div>
  );
}
